<template>
	<div>
		<Loader v-if="loading" />
		<div v-if="loaded && !loading">

			<Group title="Tools">
				<div class="border flex small-margin-bottom">
					<div class="border-right small-padding" style="width: 260px;">
						<button :disabled="saving" v-on:click="autoSelect();" class="full-width">Auto-select</button>
					</div>
					<div class="small-padding flex-grow flex align-items">
						<div>
							Update the order's shipping information to follow customer's default shipping.
							Customer's default shipping will be created, if needed.
						</div>
					</div>
				</div>
			</Group>

			<Group title="Details" class="margin-top">
				<!-- <button v-on:click="load()">ReLoad</button> -->

				<Loader v-if="soft_loading" :soft="true" />

				<div class="flex">
					<div class="flex-basis">
						<div class="input-row small-margin-bottom">
							<span class="label">Provider code</span>
							<div>{{ shipping.provider_code }}</div>
						</div>
						<div class="input-row small-margin-bottom">
							<span class="label">Parcelshop</span>
							<div>{{ shipping.parcelshop_id }}</div>
						</div>
						<div class="input-row small-margin-bottom">
							<span class="label">Outbound status</span>
							<div>
								<template v-if="!shipping.outbound">Not created</template>
								<template v-else>
									<template v-if="shipping.outbound.verified_at && !shipping.outbound.pushed_to_ftp_at">Verified (not pushed)</template>
									<template v-if="shipping.outbound.pushed_to_ftp_at">Pushed to FTP</template>
								</template>
							</div>
						</div>
					</div>
					<div class="flex-basis">
						<div>
							<div class="flex">
								<MaterialText v-model="street_name" label="Street name" />
								<MaterialText v-model="shipping.street_number" label="Street number" />
							</div>
							<div class="margin-top flex">
								<MaterialText v-model="shipping.door" label="House/door number" />
							</div>
							<ValidationObserver ref="form" class="flex margin-top">
                <ValidationProvider
                  v-slot="{ errors }"
                  :rules="{ required: true, zipcode: { regex: zipcodeStyle } }">
                  <div class="relative">
                    <MaterialText v-on:input="zipcodeInput()" :disabled="inputDisabled" v-model="shipping.zipcode" label="Zipcode" class="small-margin-right"/>
                    <span
                      v-show="shipping.zipcode"
                      class="zipcode-clear"
                      @click="clearZipcode"
                    >
                      ✖
                    </span>
                  </div>
                  <label class="text-error">{{ errors[0] }}</label>
                </ValidationProvider>
								<MaterialText v-model="shipping.city" label="City" />
							</ValidationObserver>
							<div class="margin-top">
								<MaterialDropdown label="Country" :options="countries" v-model="shipping.country_id" />
							</div>
							<div class="margin-top">
								<MaterialToggle v-model="save_to_customer" label="Set as customer's default shipping details" />
							</div>
							<div>
								<button v-on:click="saveAddress()" class="small-margin-top" :disabled="saving">Save</button>
							</div>
						</div>
					</div>
				</div>
			</Group>

			<Group title="Change parcelshop" class="margin-top">
				<div class="flex">

					<div class="flex small-margin-right">
						<MaterialDropdown label="Shipping method" :options="parcelshopShippingMethods" v-model="parcelshop_shipping_method_id" v-on:change="parcelshops = []" class="small-margin-right" />
						<MaterialText v-on:enter="search()" :disabled="saving" v-model="parcelshop_street_name" label="Street Name" />
						<MaterialText v-on:enter="search()" :disabled="saving" v-model="parcelshop_street_number" label="Street Number" />
						<MaterialText v-on:enter="search()" :disabled="saving" v-model="parcelshop_zipcode" label="Zipcode" class="small-margin-left short" />
					</div>

					<button v-on:click="search()">Search</button>
					<div class="margin-left flex-grow flex flex-end">
						<MaterialToggle v-model="save_to_customer" label="Set as customer's default parcelshop" />
					</div>
				</div>
				<div class="margin-top" v-if="parcelshops.length">
					<table class="basic">
						<thead>
							<tr>
								<th>ID</th>
								<th>Parcelshop</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="parcelshop in parcelshops">
								<td>{{ parcelshop.id }}</td>
								<td>{{ parcelshop.name }}</td>
								<td>
									<button :disabled="saving" v-on:click="setParcelshop(parcelshop.id)" class="smaller white">Select</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</Group>
		</div>
		<div v-if="!loading && !loaded">
			<button v-on:click="load()">Load</button>
		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

require('./ValidationRules.js')

export default {
  components: {
    ValidationProvider,
    ValidationObserver
  },
  props: [
    'order',
    'autoload'
  ],
  data: () => ({
    saving: false,
    loading: false,
    soft_loading: false,
    shipping: {},
    parcelshops: [],
    save_to_customer: false,
    parcelshop_street_name: '',
    parcelshop_street_number: '',
    parcelshop_zipcode: '',
    parcelshop_shipping_method_id: null,
    shipping_street_name: '',
    inputDisabled: false,
    zipcode_regex: {
      1: '/\\d{4}/', // da_DK:
      2: '/\\d{3}\\s?\\d{2}/', // sv_SE:
      3: '/\\d{4}\\s?[a-zA-Z]{2}/', // nl_NL:
      4: '/^\\d{5}$/' // de_DE:
    }
  }),
  computed: {
    ...mapGetters([
      'getCountries',
      'getParcelshopShippingMethods'
    ]),
    parcelshopShippingMethods () {
      const methods = this.getParcelshopShippingMethods.filter(method => Number(method.country_id) === Number(this.shipping.country_id))
      return Vue.$array.keyValue(methods, 'id', 'name')
    },
    zipcodeStyle () {
      try {
        if (this.shipping) {
          const obj = this.zipcodeRegex[this.shipping.country_id]
          return new RegExp(obj.replace(/\//g, ''))
        } else {
          const obj = this.zipcodeRegex[1]
          return new RegExp(obj.replace(/\//g, ''))
        }
      } catch (error) {
        const obj = this.zipcode_regex[this.shipping.country_id]
        return new RegExp(obj.replace(/\//g, ''))
      }
    },
    loaded () {
      return Object.keys(this.shipping).length > 0
    },
    countries () {
      return Vue.$array.keyValue(this.getCountries, 'id', 'name')
    },
    zipcodeRegex () {
      return Vue.$array.keyValue(this.getCountries, 'id', 'zipcode_regex')
    },
    parcelshop_address () {
      return this.parcelshop_street_name + ' ' + this.parcelshop_street_number
    },
    /**
     * @see https://app.clickup.com/t/345z660
     * Relevant to fix street number and door
     */
    street_name: {
      get: function () {
        return this.shipping_street_name
      },
      set: function (value) {
        if (value.toLowerCase().includes('packstation') && this.shipping.country_id === 4) {
          alert('Please input correct Street name.')
          return
        }
        const splitStreet = value.split(',')
        const door = splitStreet.length > 1 ? splitStreet[splitStreet.length - 1].trim() : ''

        const splitHouse = splitStreet.length > 0 ? splitStreet[0].split(' ') : splitStreet.split(' ')
        const street_number = splitHouse.length > 0 ? this.isNumber(splitHouse[splitHouse.length - 1].charAt()) ? splitHouse[splitHouse.length - 1] : '' : ''

        const streetName = value.replace(/([a-zA-Z0-9\.\- ]+) \d+[a-zA-Z]*(,? (.*))?/, '$1')

        const removeIndex = value.indexOf(street_number || (door || ''))
        this.shipping_street_name = (street_number || door) ? value.substring(0, removeIndex).trim() : streetName
        this.shipping.street_number = this.shipping.street_number ? this.shipping.street_number : street_number
        this.shipping.door = this.shipping.door ? this.shipping.door : door
      }
    }
  },
  methods: {
    isNumber (n) { return !isNaN(parseFloat(n)) && isFinite(n) },
    autoSelect () {
      Vue.$api.post('/order/' + this.order + '/shipping/auto-select', {}, {
        loader: toggle => this.saving = toggle,
        callback: () => this.load()
      })
    },
    zipcodeInput () {
      const zipcode = this.shipping.zipcode
      if (!zipcode || zipcode.length === 0) return

      if (zipcode.match(this.zipcodeStyle)) {
        this.inputDisabled = true
      } else {
        this.inputDisabled = false
      }
    },
    clearZipcode () {
      this.shipping.zipcode = ''
      this.inputDisabled = false
    },
    search () {
      var url = '/shipping/parcelshops?street=' + this.parcelshop_address +
					'&zipcode=' + this.parcelshop_zipcode +
					'&locale_id=' + this.shipping.country_id

      if (this.parcelshop_shipping_method_id) {
        url = '/dashboard/shipping/parcelshops?shipping_method_id=' + this.parcelshop_shipping_method_id +
					'&street=' + this.parcelshop_street_name +
					'&street_number=' + this.parcelshop_street_number +
					'&zipcode=' + this.parcelshop_zipcode
      }

      Vue.$api.get(url, {}, {
        loader: toggle => this.soft_loading = toggle,
        callback: data => this.parcelshops = data
      })
    },
    setParcelshop (id) {
      const carrierChanged = Number(this.parcelshop_shipping_method_id) !== Number(this.shipping.shipping_method_id)

      Vue.$api.patch('/order/' + this.order + '/parcelshop', {
        parcelshop_id: id,
        save_to_customer: this.save_to_customer,
        shipping_method_id: carrierChanged ? this.parcelshop_shipping_method_id : undefined
      }, {
        loader: toggle => this.saving = toggle,
        callback: () => this.load()
      })
    },
    saveAddress () {
      if (this.shipping_street_name.toLowerCase().includes('packstation') && this.shipping.country_id === 4) {
        alert('Please input correct Street name.')
        return
      }
      this.$refs.form.validate().then((success) => {
        if (success) {
          Vue.$api.patch('/order/' + this.order + '/shipping', {
            street_name: this.shipping_street_name,
            street_number: this.shipping.street_number,
            door: this.shipping.door,
            zipcode: this.shipping.zipcode,
            city: this.shipping.city,
            save_to_customer: this.save_to_customer,
            country_id: this.shipping.country_id
          }, {
            loader: toggle => this.saving = toggle,
            callback: () => this.load()
          })
        }
      })
    },
    load () {
      Vue.$api.get('/order/' + this.order + '/shipping', {}, {
        loader: toggle => this.loading = toggle,
        callback: data => this.parse(data)
      })
    },
    parse (data) {
      this.shipping = data
      this.shipping_street_name = data.street_name
      this.parcelshop_street_name = data.street_name
      this.parcelshop_street_number = data.street_number
      this.parcelshop_zipcode = data.zipcode
      this.parcelshop_shipping_method_id = this.parcelshopShippingMethods[data.shipping_method_id] ? data.shipping_method_id : null
    }
  },
  created () {
    if (this.autoload) {
      this.load()
    }
  }
}
</script>

<style lang="less" scoped>

.zipcode-clear {
  display: inline-block;
  width: 20px;
  height: 20px;
  text-align: center;
  position: absolute;
  right: 10px;
  top: 50%;
  z-index: 23;
  transform: translate(-50%, -50%);
  cursor: pointer;
}
</style>
